import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  DatePicker,
  message,
  Divider,
  Button,
  Modal,
  Input,
  Select,
  Table,
  Tabs,
  Tag,
  Space,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import dayjs, { Dayjs } from "dayjs";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { BASE_API_URL } from "../../utils/consts";
import { startOfMonth, endOfMonth } from "date-fns";
import "./Business.scss";
import { useTranslation } from "react-i18next";
import { isAdmin, isSuperAdmin } from "../../utils/permissions";
import { usePermissions } from "../../utils/usePermissions";
import { getUserWorkLocationsHelper } from "../../utils/helpers/workLocationsHelper";
import { isLocationActive } from "../../utils/helpers/locationVisibility";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

// =====================
//       TYPES
// =====================
interface Supplier {
  _id: string;
  name: string;
}

interface SubType {
  _id: string;
  name: string;
}

interface Category {
  _id: string;
  name: string;
  suppliers: Supplier[];
  subTypes: SubType[];
}

interface Expense {
  _id: string;
  amount: number;
  date: string;
  description?: string;

  categoryId: Category;
  locationIds: any;
  status?: any;
  supplierId?: string;
  supplier?: {
    _id: string;
    name: string;
  } | null;

  subTypeId?: string;
  subType?: {
    _id: string;
    name: string;
  } | null;

  expenseType: string;
  invoiceNumber?: string;
}

interface RevenueSummary {
  totalSales: number;
  totalDeductions: number;
  netFromRevenue: number;
  breakdown: {
    cash: number;
    card: number;
    delivery: number;
    bankTransfer: number;
  };
}

// =====================
//     COMPONENT
// =====================
const Business: React.FC = () => {
  const userData = useSelector((state: any) => state.userData.user);

  const [income, setIncome] = useState(0); // ukupni prihodi iz dailyRevenue
  const [expense, setExpense] = useState(0); // odbici iz dailyRevenue
  const [summary, setSummary] = useState<RevenueSummary | null>(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<any>(null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [expensesList, setExpensesList] = useState<Expense[]>([]);

  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [supplierFilter, setSupplierFilter] = useState<string>("all");
  const [subTypeFilter, setSubTypeFilter] = useState<string>("all");
  const [locations, setLocations] = useState<any[]>([]);
  const [locationFilters, setLocationFilters] = useState<string[]>([]);
  const [userWorkLocations, setUserWorkLocations] = useState<any[]>([]);
  const workLocationsState = useSelector((state: any) => state.workLocations);
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const canViewSummary =
    isSuperAdmin(userData?.roles) || hasPermission("business:view-summary");
  const canViewExpenses =
    isSuperAdmin(userData?.roles) || hasPermission("business:view-expenses");
  const canCreateExpense =
    isSuperAdmin(userData?.roles) || hasPermission("business:create-expense");
  const canUpdateExpense =
    isSuperAdmin(userData?.roles) || hasPermission("business:update-expense");
  const canDeleteExpense =
    isSuperAdmin(userData?.roles) || hasPermission("business:delete-expense");
  const canFilterByLocation =
    isSuperAdmin(userData?.roles) ||
    hasPermission("business:filter-by-location");
  const canFilterByCategory =
    isSuperAdmin(userData?.roles) ||
    hasPermission("business:filter-by-category");

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs(startOfMonth(new Date())),
    dayjs(endOfMonth(new Date())),
  ]);

  // MODAL
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newExpense, setNewExpense] = useState({
    amount: "",
    date: dayjs().format("YYYY-MM-DD"),
    categoryId: "",
    supplierId: "",
    subTypeId: "",
    description: "",
    expenseType: "cash",
    invoiceNumber: "",
    status: "paid",
    locationIds: [],
  });

  const selectedCategoryObj = useMemo(() => {
    return categories.find((c) => c._id === newExpense.categoryId);
  }, [newExpense.categoryId, categories]);

  // ================================
  // HELPERS ZA RESET
  // ================================
  const resetNewExpense = () => {
    setNewExpense({
      amount: "",
      date: dayjs().format("YYYY-MM-DD"),
      categoryId: "",
      supplierId: "",
      subTypeId: "",
      description: "",
      expenseType: "cash",
      invoiceNumber: "",
      status: "paid",
      locationIds: [],
    });
  };

  useEffect(() => {
    if (userData?.clientId) {
      axios
        .get(`${BASE_API_URL}/${userData?.clientId}/locations`)
        .then((res) => setLocations((res.data || []).filter(isLocationActive)))
        .catch((err) => console.error(err));
    }
  }, [userData]);

  // Load user work locations and auto-select them
  useEffect(() => {
    const loadUserWorkLocations = async () => {
      const userId = userData?._id || userData?.id;

      if (!userData?.accessToken || !userId) {
        return;
      }

      try {
        const workLocs = await getUserWorkLocationsHelper(
          userId,
          userData.accessToken,
          dispatch,
          workLocationsState,
        );
        setUserWorkLocations(workLocs);

        // Auto-select all assigned work locations
        // Only for non-super admins who have work locations assigned
        if (!isSuperAdmin(userData?.roles) && workLocs.length > 0) {
          const workLocationIds = workLocs.map((wl: any) => wl._id.toString());
          setLocationFilters(workLocationIds);
        }
      } catch (error) {
        console.error("Error loading user work locations:", error);
      }
    };

    loadUserWorkLocations();
  }, [userData, dispatch, workLocationsState]);

  useEffect(() => {
    setSupplierFilter("all");
    setSubTypeFilter("all");
  }, [categoryFilter]);

  const resetEditingExpense = () => {
    setEditingExpense(null);
  };

  const formatProtectedValue = (value: number, roles: any) => {
    if (isAdmin(roles) || isSuperAdmin(roles)) {
      return value.toLocaleString("sr-RS");
    }
    return "***";
  };

  // ================================
  // MONTH NAVIGATION
  // ================================
  const handlePrevMonth = () => {
    const newMonth = selectedMonth.subtract(1, "month");
    setSelectedMonth(newMonth);

    setDateRange([
      dayjs(startOfMonth(newMonth.toDate())),
      dayjs(endOfMonth(newMonth.toDate())),
    ]);
  };

  const handleNextMonth = () => {
    const newMonth = selectedMonth.add(1, "month");
    setSelectedMonth(newMonth);

    setDateRange([
      dayjs(startOfMonth(newMonth.toDate())),
      dayjs(endOfMonth(newMonth.toDate())),
    ]);
  };

  // ================================
  // EDIT MODAL
  // ================================
  const openEditModal = (exp: Expense) => {
    if (!canUpdateExpense) {
      message.warning(t("No permission to update expense"));
      return;
    }
    setEditingExpense({
      _id: exp._id,
      amount: exp.amount,
      date: exp.date.substring(0, 10),
      categoryId: exp.categoryId._id,
      supplierId: exp.supplier?._id || "",
      subTypeId: exp.subType?._id || "",
      description: exp.description || "",
      expenseType: exp.expenseType,
      invoiceNumber: exp.invoiceNumber || "",
      status: exp.status || "paid",
      locationIds: Array.isArray(exp.locationIds)
        ? exp.locationIds.map((l: any) => l._id)?.toString()
        : [],
    });

    setEditModalOpen(true);
  };

  const updateExpense = async () => {
    if (!canUpdateExpense) {
      message.warning(t("No permission to update expense"));
      return;
    }
    if (!editingExpense.amount || !editingExpense.categoryId) {
      return message.warning("Unesite iznos i kategoriju");
    }

    try {
      await axios.put(
        `${BASE_API_URL}/business/expenses/${editingExpense._id}`,
        {
          clientId: userData.clientId,
          date: editingExpense.date,
          amount: Number(editingExpense.amount),
          categoryId: editingExpense.categoryId,
          subTypeId: editingExpense.subTypeId,
          description: editingExpense.description,
          expenseType: editingExpense.expenseType,
          invoiceNumber: editingExpense.invoiceNumber,
          supplierId: editingExpense.supplierId,
          status: editingExpense.status,
          locationIds: editingExpense.locationIds,
        },
      );

      message.success("Rashod izmijenjen");
      setEditModalOpen(false);
      resetEditingExpense();
      fetchExpenses();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteExpense = async (id: string) => {
    if (!canDeleteExpense) {
      message.warning(t("No permission to delete expense"));
      return;
    }
    Modal.confirm({
      title: "Obrisati rashod?",
      okText: "Da",
      okType: "danger",
      cancelText: "Ne",
      onOk: async () => {
        try {
          await axios.delete(`${BASE_API_URL}/business/expenses/${id}`);
          message.success("Rashod obrisan");
          fetchExpenses();
        } catch (err) {
          console.error(err);
        }
      },
    });
  };

  // ================================
  // FETCH CATEGORIES
  // ================================
  const fetchCategories = async () => {
    if (!userData?.clientId) return;

    try {
      const { data } = await axios.get(
        `${BASE_API_URL}/admin/expense-categories`,
        {
          params: { clientId: userData.clientId },
        },
      );

      setCategories(Array.isArray(data.categories) ? data.categories : []);
    } catch (err) {
      console.error(err);
    }
  };

  // ================================
  // FETCH EXPENSES
  // ================================
  const fetchExpenses = async () => {
    try {
      const { data } = await axios.get(`${BASE_API_URL}/business/expenses`, {
        params: {
          clientId: userData.clientId,
          startDate: dateRange[0]?.format("YYYY-MM-DD"),
          endDate: dateRange[1]?.format("YYYY-MM-DD"),
        },
      });

      setExpensesList(data.expenses);
    } catch (err) {
      console.error(err);
    }
  };

  // ================================
  // ADD EXPENSE
  // ================================
  const createExpense = async () => {
    if (!canCreateExpense) {
      message.warning(t("No permission to create expense"));
      return;
    }
    if (!newExpense.amount || !newExpense.categoryId) {
      return message.warning("Unesite iznos i kategoriju");
    }

    try {
      await axios.post(`${BASE_API_URL}/business/expenses`, {
        clientId: userData.clientId,
        date: newExpense.date,
        amount: Number(newExpense.amount),
        categoryId: newExpense.categoryId,
        subTypeId: newExpense.subTypeId,
        description: newExpense.description,
        expenseType: newExpense.expenseType,
        invoiceNumber: newExpense.invoiceNumber,
        supplierId: newExpense.supplierId,
        status: newExpense.status,
        locationIds: newExpense.locationIds,
      });

      message.success("Rashod dodat");
      setAddModalOpen(false);
      resetNewExpense();
      fetchExpenses();
    } catch (err) {
      console.error(err);
    }
  };

  // ================================
  // FETCH daily revenue ONLY
  // ================================
  const fetchDailyRevenue = async (start: Dayjs | null, end: Dayjs | null) => {
    if (!userData?.clientId || !start || !end) return;
    if (!canViewSummary) return;

    try {
      const locationId = locationFilters[0] || undefined;
      const { data } = await axios.get(
        `${BASE_API_URL}/admin/${userData.clientId}/getRevenueByRange`,
        {
          params: {
            startDate: start.format("YYYY-MM-DD"),
            endDate: end.format("YYYY-MM-DD"),
            ...(locationId ? { locationId } : {}),
          },
        },
      );

      let revenues = data.revenues ?? [];
      // FILTER PO LOKACIJI (front-end SAM filtrira)
      if (locationFilters.length > 0) {
        revenues = revenues.filter((r: any) =>
          locationFilters.includes(r.locationId?._id),
        );
      }

      let totalSales = 0;
      let totalDeductions = 0;

      let cashTotal = 0;
      let cardTotal = 0;
      let deliveryTotal = 0;
      let bankTransferTotal = 0;

      revenues.forEach((r: any) => {
        const cash = Number(r.cash || 0) + Number(r.cashAmount || 0); // sve sto je keš
        const card = Number(r.cardAmount || 0);
        const delivery = Number(r.delivery || 0);
        const bank = Number(r.bankTransfer || 0);

        cashTotal += cash;
        cardTotal += card;
        deliveryTotal += delivery;
        bankTransferTotal += bank;

        totalSales += cash + card + delivery + bank;

        if (Array.isArray(r.paymentFromTheRevenue)) {
          totalDeductions += r.paymentFromTheRevenue
            .filter((item: any) => !item.other) // ignoriši "other"
            .reduce((s: number, item: any) => s + Number(item.total || 0), 0);
        }
      });

      setIncome(totalSales);
      setExpense(totalDeductions);

      setSummary({
        totalSales,
        totalDeductions,
        netFromRevenue: totalSales - totalDeductions,
        breakdown: {
          cash: cashTotal,
          card: cardTotal,
          delivery: deliveryTotal,
          bankTransfer: bankTransferTotal,
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  // ON LOAD
  useEffect(() => {
    if (!userData?.clientId) return;
    fetchCategories();
  }, [userData?.clientId]);

  useEffect(() => {
    fetchExpenses();
    fetchDailyRevenue(dateRange[0], dateRange[1]);
  }, [dateRange, userData?.clientId, locationFilters]);

  const renderLocations = (expense: any) => {
    const locs = Array.isArray(expense.locationIds) ? expense.locationIds : [];

    if (locs.length === 0) return "-";
    if (locs.length === 1) return locs[0].name;

    const first = locs[0].name;
    const extra = locs.length - 1;

    return (
      <span>
        {first}{" "}
        <Tag color="blue" style={{ borderRadius: 6 }}>
          +{extra}
        </Tag>
      </span>
    );
  };

  // ======================
  //  TABLE COLUMNS
  // ======================
  const expenseColumns: any = [
    {
      title: t("date"),
      dataIndex: "date",
      width: 110,
      render: (v: string) => dayjs(v).format("DD.MM.YYYY"),
    },
    {
      title: t("amount"),
      dataIndex: "amount",
      width: 120,
      sorter: (a: any, b: any) => Number(a.amount) - Number(b.amount),
      render: (v: number) => `${v.toLocaleString("sr-RS")} RSD`,
    },
    {
      title: t("category"),
      dataIndex: ["categoryId", "name"],
      width: 150,
    },
    {
      title: t("supplier"),
      dataIndex: "supplier",
      width: 140,
      render: (supplier: any) => supplier?.name || "-",
    },
    {
      title: t("subType"),
      dataIndex: "subType",
      width: 140,
      render: (subType: any) => subType?.name || "-",
    },
    {
      title: t("location"),
      dataIndex: "locationIds",
      width: 150,
      render: (_: any, row: Expense) => renderLocations(row),
    },
    {
      title: t("paymentType"),
      dataIndex: "expenseType",
      width: 120,
      render: (v: string) =>
        v === "cash" ? (
          <Tag color="green">{t("cash")}</Tag>
        ) : (
          <Tag color="blue">{t("onlineInvoice")}</Tag>
        ),
    },
    {
      title: t("invoiceNumber"),
      dataIndex: "invoiceNumber",
      width: 120,
      ellipsis: true,
      render: (inv: string) => inv || "-",
    },
    {
      title: t("status"),
      dataIndex: "status",
      width: 100,
      render: (v: string) =>
        v === "paid" ? (
          <Tag color="green">{t("paid")}</Tag>
        ) : (
          <Tag color="red">{t("unpaid")}</Tag>
        ),
    },
    {
      title: t("description"),
      dataIndex: "description",
      ellipsis: true,
      width: 200,
      render: (v: string) => v || "-",
    },
    {
      title: t("actions"),
      width: 120,
      fixed: "right",
      render: (_: any, row: Expense) => (
        <Space size="small">
          <Button size="small" onClick={() => openEditModal(row)}>
            {t("edit")}
          </Button>
          <Button danger size="small" onClick={() => deleteExpense(row._id)}>
            {t("delete")}
          </Button>
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 150,
      render: (_: any, record: Expense) => (
        <Space>
          {canUpdateExpense && (
            <Button type="link" onClick={() => openEditModal(record)}>
              {t("Edit")}
            </Button>
          )}
          {canDeleteExpense && (
            <Button
              type="link"
              danger
              onClick={() => deleteExpense(record._id)}
            >
              {t("Delete")}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const filteredByLocation = useMemo(() => {
    if (locationFilters.length === 0) return expensesList;

    return expensesList.filter((e: any) => {
      // slučaj 1 → expense.locationId = "single"
      if (e.locationId?._id) {
        return locationFilters.includes(e.locationId._id);
      }

      // slučaj 2 → expense.locationIds = array
      if (Array.isArray(e.locationIds)) {
        return e.locationIds.some((loc: any) =>
          locationFilters.includes(loc._id),
        );
      }

      return false;
    });
  }, [expensesList, locationFilters]);

  const filteredExpenses = useMemo(() => {
    return filteredByLocation.filter((e) => {
      const categoryMatch =
        categoryFilter === "all" || e.categoryId?._id === categoryFilter;

      const supplierMatch =
        supplierFilter === "all" || e.supplier?._id === supplierFilter;

      const subTypeMatch =
        subTypeFilter === "all" || e.subType?._id === subTypeFilter;

      return categoryMatch && supplierMatch && subTypeMatch;
    });
  }, [categoryFilter, supplierFilter, subTypeFilter, filteredByLocation]);

  const totalExpensesAmount = useMemo(() => {
    return filteredByLocation.reduce(
      (sum, exp) => sum + Number(exp.amount || 0),
      0,
    );
  }, [filteredByLocation]);

  // totalDeductions = odbici iz dnevnog prometa + svi eksterni rashodi
  const totalDeductions = expense + totalExpensesAmount;
  const total = income - totalDeductions;

  const categoryTabs = [
    { key: "all", label: "Sve kategorije" },
    ...categories.map((c) => ({
      key: c._id,
      label: c.name,
    })),
  ];

  if (!userData?.clientId) return null;
  if (!canViewSummary && !canViewExpenses) {
    return <div className="mainContainer">{t("No permission to view")}</div>;
  }

  return (
    <div className="business-page">
      {/* HEADER */}
      <div className="business-header">
        <Title level={2} style={{ margin: 0, whiteSpace: "nowrap" }}>
          Poslovanje
        </Title>

        <div className="business-controls">
          <Button
            icon={<LeftOutlined />}
            onClick={handlePrevMonth}
            type="text"
          />

          <RangePicker
            value={dateRange}
            allowClear={false}
            format="DD.MM.YYYY"
            onCalendarChange={(v) =>
              setDateRange([v?.[0] || null, v?.[1] || null])
            }
          />

          <Button
            icon={<RightOutlined />}
            onClick={handleNextMonth}
            type="text"
          />
          <Select
            mode="multiple"
            maxTagCount="responsive"
            className="location-filter"
            style={{ width: 250 }}
            value={locationFilters.map((id) => id?.toString())}
            allowClear
            showSearch
            placeholder={t("location")}
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              (option?.children as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            onChange={(values) =>
              setLocationFilters(values.map((v: any) => v?.toString()))
            }
            disabled={
              !canFilterByLocation ||
              (userWorkLocations.length > 0 && !isSuperAdmin(userData?.roles))
            }
          >
            {locations.map((loc: any) => (
              <Option key={loc._id} value={loc._id?.toString()}>
                {loc.name}
              </Option>
            ))}
          </Select>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (!canCreateExpense) {
                message.warning(t("No permission to create expense"));
                return;
              }
              setAddModalOpen(true);
            }}
            disabled={!canCreateExpense}
          >
            Dodaj rashod
          </Button>
        </div>
      </div>

      {/* TOP CARDS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card className="dashboard-card" bordered={false}>
            <Statistic
              title="Prihodi"
              value={formatProtectedValue(income, userData?.roles)}
              valueStyle={{ color: "#1677ff" }}
              prefix={<ArrowUpOutlined />}
              suffix="RSD"
            />
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card className="dashboard-card" bordered={false}>
            <Statistic
              title="Ukupni odbici"
              value={formatProtectedValue(totalDeductions, userData?.roles)}
              valueStyle={{ color: "#faad14" }}
              prefix={<ArrowDownOutlined />}
              suffix="RSD"
            />
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card className="dashboard-card" bordered={false}>
            <Statistic
              title="Neto rezultat"
              value={formatProtectedValue(total, userData?.roles)}
              suffix="RSD"
              valueStyle={{ color: total >= 0 ? "#52c41a" : "#cf1322" }}
            />
          </Card>
        </Col>
      </Row>

      {/* DETALJNI PREGLED PRIHODA I ODBITAKA */}
      <Card
        style={{ marginBottom: 24 }}
        bodyStyle={{ padding: 16 }}
        bordered={false}
      >
        <div className="finance-header">
          <div className="finance-title">
            <Title level={4} style={{ margin: 0 }}>
              Finansijski pregled
            </Title>
            <Tag
              icon={<InfoCircleOutlined />}
              color="blue"
              className="finance-tag"
            >
              Kako smo došli do neto iznosa
            </Tag>
          </div>
          <Text type="secondary" className="finance-period">
            Period: {dateRange[0]?.format("DD.MM.YYYY")} –{" "}
            {dateRange[1]?.format("DD.MM.YYYY")}
          </Text>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary">Prihodi</Text>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 4,
                }}
              >
                <Text strong>Ukupno</Text>
                <Text strong>
                  {summary
                    ? formatProtectedValue(summary.totalSales, userData?.roles)
                    : income.toLocaleString("sr-RS")}{" "}
                  RSD
                </Text>
              </div>
            </div>

            <div style={{ marginTop: 8 }}>
              <Text type="secondary">Struktura prihoda</Text>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <Tag>
                  Keš:{" "}
                  {summary
                    ? formatProtectedValue(
                        summary.breakdown.cash,
                        userData?.roles,
                      )
                    : 0}{" "}
                  RSD
                </Tag>
                <Tag>
                  Kartice:{" "}
                  {summary
                    ? formatProtectedValue(
                        summary.breakdown.card,
                        userData?.roles,
                      )
                    : 0}{" "}
                  RSD
                </Tag>
                <Tag>
                  Dostava:{" "}
                  {summary
                    ? formatProtectedValue(
                        summary.breakdown.delivery,
                        userData?.roles,
                      )
                    : 0}{" "}
                  RSD
                </Tag>
                <Tag>
                  Prenos na račun:{" "}
                  {summary
                    ? formatProtectedValue(
                        summary.breakdown.bankTransfer,
                        userData?.roles,
                      )
                    : 0}{" "}
                  RSD
                </Tag>
              </div>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <Space direction="vertical" style={{ width: "100%" }} size="small">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 4,
                }}
              >
                <Text type="secondary">Odbici iz dnevnog prometa</Text>
                <Text>
                  {summary
                    ? summary.totalDeductions.toLocaleString("sr-RS")
                    : expense.toLocaleString("sr-RS")}{" "}
                  RSD
                </Text>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 4,
                }}
              >
                <Text type="secondary">Eksterni rashodi (računi)</Text>
                <Text>{totalExpensesAmount.toLocaleString("sr-RS")} RSD</Text>
              </div>

              <Divider style={{ margin: "8px 0" }} />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 4,
                }}
              >
                <Text strong>Neto rezultat</Text>
                <Text
                  strong
                  style={{ color: total >= 0 ? "#52c41a" : "#cf1322" }}
                >
                  {formatProtectedValue(total, userData?.roles)} RSD
                </Text>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* KATEGORIJE (TABS) */}
      <Tabs
        activeKey={categoryFilter}
        onChange={(key) => setCategoryFilter(key)}
        items={[
          { key: "all", label: "Sve kategorije" },
          ...categories.map((c) => ({
            key: c._id,
            label: c.name,
          })),
        ]}
        tabBarStyle={{
          pointerEvents: canFilterByCategory ? "auto" : "none",
          opacity: canFilterByCategory ? 1 : 0.6,
        }}
      />

      {/* NOVI FILTERI */}
      <div
        style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}
      >
        <Select
          style={{ width: 200 }}
          value={supplierFilter === "all" ? undefined : supplierFilter}
          allowClear
          showSearch
          placeholder={t("supplier")}
          optionFilterProp="children"
          filterOption={(input, option: any) =>
            (option?.children as string)
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
          onChange={(value) => setSupplierFilter(value || "all")}
          disabled={!canFilterByCategory}
        >
          <Option value="all" disabled>
            {t("supplier")} - {t("all")}
          </Option>

          {(categoryFilter === "all"
            ? categories.flatMap((c) => c.suppliers)
            : categories.find((c) => c._id === categoryFilter)?.suppliers || []
          ).map((s) => (
            <Option key={s._id} value={s._id}>
              {s.name}
            </Option>
          ))}
        </Select>

        <Select
          style={{ width: 200 }}
          value={subTypeFilter === "all" ? undefined : subTypeFilter}
          allowClear
          showSearch
          placeholder={t("expenseType")}
          optionFilterProp="children"
          filterOption={(input, option: any) =>
            (option?.children as string)
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
          onChange={(value) => setSubTypeFilter(value || "all")}
          disabled={!canFilterByCategory}
        >
          <Option value="all" disabled>
            {t("expenseType")} - {t("all")}
          </Option>

          {(categoryFilter === "all"
            ? categories.flatMap((c) => c.subTypes)
            : categories.find((c) => c._id === categoryFilter)?.subTypes || []
          ).map((st) => (
            <Option key={st._id} value={st._id}>
              {st.name}
            </Option>
          ))}
        </Select>
      </div>

      {/* EXPENSE TABLE */}
      <Title level={3} style={{ marginTop: 8 }}>
        Rashodi
      </Title>
      {canViewExpenses ? (
        <Table
          columns={expenseColumns}
          dataSource={filteredExpenses}
          rowKey="_id"
          pagination={false}
          scroll={{ x: 1100 }} // omogućava horizontalni scroll kad ima puno kolona
          bordered
          size="middle"
          style={{ background: "#fff", borderRadius: 8 }}
        />
      ) : (
        <div style={{ marginTop: 12 }}>
          {t("No permission to view expenses")}
        </div>
      )}

      {/* ADD EXPENSE MODAL */}
      <Modal
        open={addModalOpen}
        title={t("addExpense")}
        className="standardHeader standardFooter"
        onCancel={() => {
          setAddModalOpen(false);
          resetNewExpense();
        }}
        onOk={createExpense}
        okButtonProps={{ disabled: !canCreateExpense }}
      >
        {/* DATE */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("date")}</Text>
          <DatePicker
            style={{ width: "100%" }}
            format="DD.MM.YYYY"
            value={dayjs(newExpense.date)}
            onChange={(v) =>
              setNewExpense({
                ...newExpense,
                date: v ? v.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
              })
            }
          />
        </div>

        {/* AMOUNT */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("amount")}</Text>
          <Input
            placeholder={t("amountPlaceholder")}
            type="number"
            value={newExpense.amount}
            onChange={(e) =>
              setNewExpense({ ...newExpense, amount: e.target.value })
            }
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("amountInfo")}
          </Text>
        </div>

        {/* CATEGORY */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("category")}</Text>
          <Select
            placeholder={t("categoryPlaceholder")}
            showSearch
            optionFilterProp="children"
            value={newExpense.categoryId}
            onChange={(v) =>
              setNewExpense({
                ...newExpense,
                categoryId: v,
                supplierId: "",
                subTypeId: "",
              })
            }
            style={{ width: "100%" }}
          >
            {categories?.map((c) => (
              <Option key={c._id} value={c._id}>
                {c.name}
              </Option>
            ))}
          </Select>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("categoryInfo")}
          </Text>
        </div>

        {/* LOCATION */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("location")}</Text>
          <Select
            mode="multiple"
            placeholder={t("location")}
            style={{ width: "100%" }}
            value={newExpense.locationIds}
            onChange={(v) => setNewExpense({ ...newExpense, locationIds: v })}
          >
            {locations.map((loc) => (
              <Option key={loc._id} value={loc._id?.toString()}>
                {loc.name}
              </Option>
            ))}
          </Select>
        </div>

        {/* SUPPLIER */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("supplier")}</Text>
          <Select
            placeholder={t("supplierPlaceholder")}
            showSearch
            optionFilterProp="children"
            value={newExpense.supplierId}
            onChange={(v) => setNewExpense({ ...newExpense, supplierId: v })}
            style={{ width: "100%" }}
            disabled={!selectedCategoryObj}
          >
            {selectedCategoryObj?.suppliers?.map((sup) => (
              <Option key={sup._id} value={sup._id}>
                {sup.name}
              </Option>
            ))}
          </Select>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("supplierInfo")}
          </Text>
        </div>

        {/* SUBTYPE */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("expenseType")}</Text>
          <Select
            placeholder={t("expenseTypePlaceholder")}
            showSearch
            optionFilterProp="children"
            value={newExpense.subTypeId}
            onChange={(v) => setNewExpense({ ...newExpense, subTypeId: v })}
            style={{ width: "100%" }}
            disabled={!selectedCategoryObj}
          >
            {selectedCategoryObj?.subTypes?.map((st) => (
              <Option key={st._id} value={st._id}>
                {st.name}
              </Option>
            ))}
          </Select>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("expenseTypeInfo")}
          </Text>
        </div>

        {/* TYPE */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("paymentType")}</Text>
          <Select
            placeholder={t("paymentTypePlaceholder")}
            value={newExpense.expenseType}
            onChange={(v) => setNewExpense({ ...newExpense, expenseType: v })}
            style={{ width: "100%" }}
          >
            <Option value="cash">{t("cash")}</Option>
            <Option value="online">{t("onlineInvoice")}</Option>
          </Select>
        </div>

        {/* INVOICE */}
        {newExpense.expenseType === "online" && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>{t("invoiceNumber")}</Text>
            <Input
              placeholder={t("invoiceNumberPlaceholder")}
              value={newExpense.invoiceNumber}
              onChange={(e) =>
                setNewExpense({ ...newExpense, invoiceNumber: e.target.value })
              }
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("invoiceNumberInfo")}
            </Text>
          </div>
        )}

        {/* STATUS */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("paymentStatus")}</Text>
          <Select
            value={newExpense.status}
            onChange={(v) => setNewExpense({ ...newExpense, status: v })}
            style={{ width: "100%" }}
          >
            <Option value="paid">{t("paid")}</Option>
            <Option value="unpaid">{t("unpaid")}</Option>
          </Select>
        </div>

        {/* DESCRIPTION */}
        <div style={{ marginBottom: 12 }}>
          <Text strong>{t("description")}</Text>
          <Input.TextArea
            placeholder={t("descriptionPlaceholder")}
            value={newExpense.description}
            onChange={(e) =>
              setNewExpense({ ...newExpense, description: e.target.value })
            }
            rows={3}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("descriptionInfo")}
          </Text>
        </div>
      </Modal>

      {/* EDIT EXPENSE MODAL */}
      <Modal
        open={editModalOpen}
        title={t("editExpense")}
        className="standardHeader standardFooter"
        onCancel={() => {
          setEditModalOpen(false);
          resetEditingExpense();
        }}
        onOk={updateExpense}
        okText={t("update")}
        cancelText={t("cancel")}
        okButtonProps={{ disabled: !canUpdateExpense }}
      >
        {editingExpense && (
          <>
            {/* DATE */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t("date")}</Text>
              <DatePicker
                style={{ width: "100%" }}
                format="DD.MM.YYYY"
                value={dayjs(editingExpense.date)}
                onChange={(v) =>
                  setEditingExpense({
                    ...editingExpense,
                    date: v ? v.format("YYYY-MM-DD") : editingExpense.date,
                  })
                }
              />
            </div>

            {/* AMOUNT */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t("amount")}</Text>
              <Input
                placeholder={t("amountPlaceholder")}
                type="number"
                value={editingExpense.amount}
                onChange={(e) =>
                  setEditingExpense({
                    ...editingExpense,
                    amount: e.target.value,
                  })
                }
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("amountInfo")}
              </Text>
            </div>

            {/* CATEGORY */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t("category")}</Text>
              <Select
                placeholder={t("categoryPlaceholder")}
                value={editingExpense.categoryId}
                onChange={(v) =>
                  setEditingExpense({
                    ...editingExpense,
                    categoryId: v,
                    supplierId: "",
                    subTypeId: "",
                  })
                }
                style={{ width: "100%" }}
              >
                {categories.map((c) => (
                  <Option key={c._id} value={c._id}>
                    {c.name}
                  </Option>
                ))}
              </Select>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("categoryInfo")}
              </Text>
            </div>

            {/* LOCATION */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t("location")}</Text>
              <Select
                mode="multiple"
                placeholder={t("location")}
                style={{ width: "100%" }}
                value={editingExpense.locationIds}
                onChange={(v) =>
                  setEditingExpense({ ...editingExpense, locationIds: v })
                }
              >
                {locations.map((loc) => (
                  <Option key={loc._id} value={loc._id}>
                    {loc.name}
                  </Option>
                ))}
              </Select>
            </div>

            {/* SUPPLIER */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t("supplier")}</Text>
              <Select
                placeholder={t("supplierPlaceholder")}
                value={editingExpense.supplierId}
                onChange={(v) =>
                  setEditingExpense({ ...editingExpense, supplierId: v })
                }
                style={{ width: "100%" }}
                disabled={!editingExpense.categoryId}
              >
                {categories
                  .find((c) => c._id === editingExpense.categoryId)
                  ?.suppliers.map((sup) => (
                    <Option key={sup._id} value={sup._id}>
                      {sup.name}
                    </Option>
                  ))}
              </Select>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("supplierInfo")}
              </Text>
            </div>

            {/* SUBTYPE */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t("expenseType")}</Text>
              <Select
                placeholder={t("expenseTypePlaceholder")}
                value={editingExpense.subTypeId}
                onChange={(v) =>
                  setEditingExpense({ ...editingExpense, subTypeId: v })
                }
                style={{ width: "100%" }}
                disabled={!editingExpense.categoryId}
              >
                {categories
                  .find((c) => c._id === editingExpense.categoryId)
                  ?.subTypes.map((st) => (
                    <Option key={st._id} value={st._id}>
                      {st.name}
                    </Option>
                  ))}
              </Select>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("expenseTypeInfo")}
              </Text>
            </div>

            {/* STATUS */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t("paymentStatus")}</Text>
              <Select
                value={editingExpense.status}
                onChange={(v) =>
                  setEditingExpense({ ...editingExpense, status: v })
                }
                style={{ width: "100%" }}
              >
                <Option value="paid">{t("paid")}</Option>
                <Option value="unpaid">{t("unpaid")}</Option>
              </Select>
            </div>

            {/* DESCRIPTION */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>{t("description")}</Text>
              <Input.TextArea
                placeholder={t("descriptionPlaceholder")}
                value={editingExpense.description}
                onChange={(e) =>
                  setEditingExpense({
                    ...editingExpense,
                    description: e.target.value,
                  })
                }
                rows={3}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("descriptionInfo")}
              </Text>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Business;
